import React from 'react';
import {
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  Divider,
  Tag,
  Button,
  Dropdown,
  Tooltip,
  Typography,
} from 'antd';
import {
  SaveOutlined,
  MailOutlined,
  MessageOutlined,
  PrinterOutlined,
  DownOutlined,
} from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { Vehicle } from '../../types';
import PartsCatalogButton from '../PartsCatalogButton';
import dayjs from 'dayjs';

const { Text } = Typography;

export interface EstimateFormFieldsProps {
  form: FormInstance;
  isMobile: boolean;
  loading: boolean;
  isFormValid: boolean;
  isEditing: boolean;
  autoSaveStatus: 'idle' | 'saving' | 'saved';
  subtotal: number;
  taxAmount: number;
  total: number;
  taxRateInput: string;
  onTaxRateChange: (value: string) => void;
  onSave: (status: 'draft' | 'sent') => void;
  selectedVehicle: Vehicle | null;
  saveDisabledReason?: string;
}

/** Bottom panel with totals, notes, tax, and save/send buttons. */
export const EstimateFormFields: React.FC<EstimateFormFieldsProps> = ({
  form,
  isMobile,
  loading,
  isFormValid,
  isEditing,
  autoSaveStatus,
  subtotal,
  taxAmount,
  total,
  taxRateInput,
  onTaxRateChange,
  onSave,
  selectedVehicle,
  saveDisabledReason,
}) => {
  const inputStyle = { fontSize: 16 };

  if (isMobile) {
    return (
      <div style={{
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        padding: '12px',
        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
        paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
        flexShrink: 0,
        zIndex: 20,
      }}>
        <Form form={form} layout="vertical">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {/* Totals row */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#f9fafb',
              padding: '10px 12px',
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Text style={{ fontSize: 12, color: '#6b7280' }}>Tax</Text>
                <Input
                  size="small"
                  value={taxRateInput}
                  onChange={(e) => onTaxRateChange(e.target.value)}
                  disabled={loading}
                  style={{ width: 52, textAlign: 'center', ...inputStyle, height: 28 }}
                  suffix={<span style={{ fontSize: 11, color: '#6b7280' }}>%</span>}
                />
              </div>
              <div style={{ textAlign: 'right' }}>
                <Text style={{ fontSize: 11, color: '#6b7280', display: 'block' }}>
                  Subtotal: ${subtotal.toFixed(2)} + Tax: ${taxAmount.toFixed(2)}
                </Text>
                <Text strong style={{ fontSize: 20, color: '#10b981' }}>${total.toFixed(2)}</Text>
              </div>
            </div>
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={() => onSave('draft')}
                loading={loading}
                disabled={loading || !isFormValid}
                size="large"
                style={{ flex: 2, height: 48, fontWeight: 600, fontSize: 15 }}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
              <Dropdown
                menu={{
                  items: [
                    { key: 'email', icon: <MailOutlined />, label: 'Send via Email', onClick: () => {}, disabled: loading },
                    { key: 'text', icon: <MessageOutlined />, label: 'Send via SMS', onClick: () => {}, disabled: loading },
                    { key: 'print', icon: <PrinterOutlined />, label: 'Print Estimate', onClick: () => {}, disabled: loading },
                  ],
                }}
                disabled={loading || !isFormValid}
              >
                <Button size="large" icon={<DownOutlined />} disabled={loading || !isFormValid} style={{ flex: 1, height: 48, fontWeight: 600, fontSize: 15 }}>
                  Send
                </Button>
              </Dropdown>
            </div>
          </div>
        </Form>
      </div>
    );
  }

  // Desktop bottom panel
  return (
    <div style={{
      borderTop: '1px solid #e5e7eb',
      backgroundColor: '#ffffff',
      padding: '12px 20px',
      boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
      flexShrink: 0,
      zIndex: 20,
    }}>
      <Form form={form} layout="vertical">
        <Row gutter={[16, 12]} align="top">
          {/* Notes Section */}
          <Col xs={24} sm={24} md={24} lg={10} xl={10}>
            <Row gutter={[12, 8]}>
              <Col xs={24} sm={12} md={8} lg={10}>
                <Form.Item
                  label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Valid Until</span>}
                  name="valid_until"
                  initialValue={dayjs().add(14, 'days')}
                  style={{ marginBottom: '0' }}
                >
                  <DatePicker
                    size="small"
                    style={{ width: '100%', ...inputStyle }}
                    placeholder="2 weeks"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={16} lg={14}>
                <Form.Item
                  label={<span style={{ fontSize: '11px', fontWeight: 500 }}>Customer Notes</span>}
                  name="customer_notes"
                  style={{ marginBottom: '0' }}
                >
                  <Input.TextArea
                    size="small"
                    rows={1}
                    placeholder="Notes for customer..."
                    style={inputStyle}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          {/* Totals Section */}
          <Col xs={24} sm={12} md={12} lg={8} xl={8}>
            <div style={{
              backgroundColor: '#f9fafb',
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #e5e7eb',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <Text style={{ fontSize: '11px', color: '#6b7280' }}>Subtotal</Text>
                <Text style={{ fontSize: '11px', fontWeight: 500 }}>${subtotal.toFixed(2)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Text style={{ fontSize: '11px', color: '#6b7280' }}>Tax</Text>
                  <Input
                    size="small"
                    value={taxRateInput}
                    onChange={(e) => onTaxRateChange(e.target.value)}
                    disabled={loading}
                    style={{ width: '60px', textAlign: 'center', fontSize: '11px', height: '24px', lineHeight: '1', padding: '4px 24px 4px 8px', display: 'flex', alignItems: 'center' }}
                    suffix={<span style={{ fontSize: '11px', color: '#6b7280' }}>%</span>}
                  />
                </div>
                <Text style={{ fontSize: '11px', fontWeight: 500 }}>${taxAmount.toFixed(2)}</Text>
              </div>
              <Divider style={{ margin: '6px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong style={{ fontSize: '13px' }}>Total</Text>
                <Text strong style={{ fontSize: '16px', color: '#10b981' }}>${total.toFixed(2)}</Text>
              </div>
            </div>
          </Col>

          {/* Action Buttons */}
          <Col xs={24} sm={12} md={12} lg={6} xl={6}>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
              {!isEditing && autoSaveStatus === 'saving' && (
                <Tag color="processing" style={{ marginRight: 8 }}>Auto-saving...</Tag>
              )}
              {!isEditing && autoSaveStatus === 'saved' && (
                <Tag color="success" style={{ marginRight: 8 }}>Auto-saved</Tag>
              )}
              <PartsCatalogButton
                vehicle={selectedVehicle || undefined}
                size="middle"
                showText={false}
              />
              <Tooltip title={saveDisabledReason}>
                <Button
                  size="middle"
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={() => onSave('draft')}
                  loading={loading}
                  disabled={loading || !isFormValid}
                >
                  {loading ? 'Saving...' : 'Save'}
                </Button>
              </Tooltip>
              <Dropdown
                menu={{
                  items: [
                    { key: 'email', icon: <MailOutlined />, label: 'Send via Email', onClick: () => {}, disabled: loading },
                    { key: 'text', icon: <MessageOutlined />, label: 'Send via Text/SMS', onClick: () => {}, disabled: loading },
                    { key: 'print', icon: <PrinterOutlined />, label: 'Print Estimate', onClick: () => {}, disabled: loading },
                  ],
                }}
                disabled={loading || !isFormValid}
              >
                <Button size="middle" icon={<DownOutlined />} disabled={loading || !isFormValid}>
                  Send
                </Button>
              </Dropdown>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
  );
};
